'use client'

import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'

// MapLibre touches `window` at import time, so it cannot be server-rendered. Everything ELSE on
// the page is server-rendered — the headline, the stats and every city link are in the HTML for
// crawlers. Only the canvas is deferred.
//
// And now it is deferred PROPERLY. `dynamic()` splits the chunk but still evaluates it on mount,
// and MapLibre is 272KB of WebGL library. See MapMount.jsx for the measurements. The national map
// is the front door's signature, but the ranked city list underneath it is the content and it is
// already in the HTML. So the map waits for an idle main thread.
const SpainMap = dynamic(() => import('./SpainMap'), {
  ssr: false,
  loading: () => (
    <div className="mapframe">
      <div className="mapcanvas mapcanvas--loading" />
    </div>
  ),
})

const idle = (cb) =>
  typeof window !== 'undefined' && 'requestIdleCallback' in window
    ? window.requestIdleCallback(cb, { timeout: 2500 })
    : setTimeout(cb, 1200)

export default function SpainMapMount(props) {
  const holder = useRef(null)
  const [load, setLoad] = useState(false)

  useEffect(() => {
    if (load) return
    let done = false
    const go = () => {
      if (done) return
      done = true
      setLoad(true)
    }

    if (typeof IntersectionObserver === 'undefined') {
      idle(go)
      return
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect()
          idle(go)
        }
      },
      { rootMargin: '400px 0px' },
    )

    if (holder.current) io.observe(holder.current)
    return () => io.disconnect()
  }, [load])

  if (!load) {
    // Same frame, same height, so nothing shifts when the real map arrives. CLS stays 0.
    return (
      <div ref={holder} className="mapframe" aria-hidden="true">
        <div className="mapcanvas mapcanvas--loading" />
      </div>
    )
  }

  return <SpainMap {...props} />
}
